import { useState, useMemo } from 'react';
import { Icons } from '../utils/icons';

// SVG icons for sections (no emojis)
const SectionIcons = {
    'getting-started': () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
    'managing-channel': () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
    'payments': () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
    'telegram-bot': () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
    'chatters': () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    'chatter-getting-started': () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
    'chatter-chatting': () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    'chatter-cc-chat': () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>,
};

const CREATOR_SECTIONS = [
    {
        id: 'getting-started',
        title: 'Getting Started',
        articles: [
            {
                id: 'what-is-privly',
                title: 'What is Privly?',
                content: `Privly helps Telegram creators monetize their communities. Connect your channel and start earning through VIP subscriptions, pay-per-view content, and tips — all without leaving Telegram.

**How creators earn:**

• **VIP Subscriptions** — Recurring payments for exclusive channel access
• **PPV Content** — One-time payments to unlock photos, videos, or files
• **Tips** — Voluntary payments from your fans

**Revenue split:** You keep **85%** of every payment. Privly takes a 15% platform fee, collected automatically at checkout. Payment processing fees (~2.9% + $0.30) come from your portion.`
            },
            {
                id: 'create-account',
                title: 'Creating Your Account',
                content: `**Email registration**

1. Go to privly.co and click **Sign Up**
2. Choose a username (3–30 characters, letters and numbers)
3. Set a password (8+ characters with lowercase, uppercase, and a symbol)
4. Enter your email address
5. Check your inbox for a 6-digit code (expires in 15 minutes)
6. Enter the code — you're in

**Google Sign-In**

Click "Continue with Google" and pick your account. Your username is created from your email prefix. If you already have an account with that email, you're logged in directly.`
            },
            {
                id: 'connect-telegram',
                title: 'Connecting Telegram',
                content: `Linking your Telegram lets the bot manage your channels and send you notifications. Privly does **not** access your Telegram login credentials, messages, or contacts — it only links your Telegram user ID for channel management.

1. In your dashboard, find the **Link Telegram** step in the onboarding tracker
2. Click it — you'll see a link to @privly_bot
3. Open the link in Telegram and tap **Start**
4. The bot confirms the connection

**What linking does:**

• Allows the bot to send you notifications about new sales and subscribers
• Lets you add channels from your Telegram account
• Enables the bot to manage VIP channel membership (adding/removing subscribers)

**What it does NOT do:**

• Does not access your Telegram password or login
• Does not read your private messages
• Does not access your contacts or other groups`
            },
            {
                id: 'add-channel',
                title: 'Adding Your Channel',
                content: `1. In the sidebar, click **Add Channel**
2. Check Telegram — the bot sends you a button
3. Click it and select your channel from the list
4. The bot joins as an admin

The bot needs admin permissions to function properly. Your channel appears in the sidebar immediately.

**Required bot permissions:**

• **Post Messages** — Send promotional posts and subscription buttons
• **Delete Messages** — Remove paywall messages after unlock
• **Ban Users** — Enforce subscription removals and bans
• **Invite Users** — Add subscribers to VIP channels via invite links
• **Manage Chat** — Handle join requests and channel settings
• **Manage Direct Messages** — Receive and respond to channel DMs`
            },
            {
                id: 'setup-subscriptions',
                title: 'Setting Up Subscriptions',
                content: `Go to the **Subscriptions** tab for your channel.

**Step 1: Connect your VIP channel**

Before setting up plans, you need a VIP channel — the private group where subscribers get added.

1. Go to the **Subscriptions** tab
2. Click **Connect Channel**
3. The bot sends you a button in Telegram
4. Click it and select your private VIP group
5. The bot joins as an admin

Make your VIP channel a private group with "Request to Join" enabled. The bot handles approvals automatically.

**Step 2: Configure your plans**

Available intervals: **Weekly**, **Monthly**, **3 Months**, and **1 Year**. Toggle each plan ON/OFF independently. Default setup: Monthly ($20/mo) and 1 Year ($200/yr) are pre-configured.

**For each plan you can set:**

• **Price** — The recurring amount (minimum $3.00, max $9,999)
• **Strikethrough price** — A higher "was" price shown crossed out to highlight the deal. Example: set price to $20 and strikethrough to $30 — subscribers see ~~$30~~ **$20/month**
• **First-period discount** — A lower price for the first billing cycle only. Example: $10 for the first month, then $20/month. Without a promo code, everyone gets the discount automatically.
• **Promo code** — Optionally require a code for the first-period discount. Set a discounted first-period price, then toggle "Require promo code" and enter your code (e.g., "WELCOME50"). Only users who enter the code get the discount.
• **Free trial** — Days of free access before the first charge (1, 7, or 30 days). Disables first-period discounts.

**Step 3: Pause subscriptions**

Use the "Accepting new subscribers" toggle to temporarily pause new signups. Existing subscribers keep their access — this only hides the subscribe page.

**Step 4: Promote**

Set up a promo banner and message in the Promo Settings section, then click "Post Promo to Channel" to share it with your audience.`
            },
            {
                id: 'verify-identity',
                title: 'Verifying Your Identity (KYC)',
                content: `Before withdrawing earnings, you need to verify your identity.

1. Click **Verify Identity** in the onboarding tracker
2. You're redirected to a verification page
3. Provide your name, address, and ID document
4. Verification usually takes a few minutes

Once verified, you can access the payout portal to withdraw earnings to your bank account.`
            },
        ]
    },
    {
        id: 'managing-channel',
        title: 'Managing Your Channel',
        articles: [
            {
                id: 'subscriber-management',
                title: 'Managing Subscribers',
                content: `The Subscriptions tab shows your subscribers with status filters.

• **Active** — Paying, has VIP channel access
• **Trialing** — Free trial, has access, hasn't paid yet
• **Past Due** — Payment failed, in 3-day grace period
• **Cancelled** — Cancelled but access remains until period ends
• **Expired** — Ended, removed from channel

Click any subscriber for details: subscription date, last payment, next billing, and payment history.`
            },
            {
                id: 'direct-messages',
                title: 'Direct Messages',
                content: `When users message your channel, conversations appear in the **DMs** tab.

**Setup:** Enable "Direct Messages" in your Telegram channel settings.

**Features**

• View and reply to subscriber messages
• Send PPV content within conversations
• Send tip requests
• Set nicknames and notes for each subscriber

**Sending PPV in DMs**

1. Open a conversation
2. Click the $ icon
3. Select a file from your Vault
4. Set a price (minimum $3)
5. Send — subscriber sees a blurred preview with an "Unlock" button`
            },
            {
                id: 'vault',
                title: 'Using the Vault',
                content: `The **Vault** stores content you'll sell.

• Drag and drop to upload photos, videos, and documents
• Organize into folders
• Files are used when sending PPV content in DMs

When sending paid content, you select files directly from your Vault.`
            },
            {
                id: 'payments-earnings',
                title: 'Payments & Earnings',
                content: `Click **Payments** to see your earnings.

• **Total Earned** — Lifetime earnings (85% of gross)
• **Last 30 Days** — With growth percentage
• **Transaction History** — Every payment with type, amount, and net earning

**Withdrawing money**

Use the payout portal in your Payments section. Add your bank account and initiate withdrawals. Identity verification is required first.`
            },
        ]
    },
    {
        id: 'payments',
        title: 'How Payments Work',
        articles: [
            {
                id: 'payment-flow',
                title: 'Subscription Flow',
                content: `When someone subscribes to your VIP channel:

1. They pick a plan and complete the payment
2. They're automatically redirected to Telegram
3. The bot sends them a link to join your VIP channel
4. They click the link and they're in

That's it. Everything is automatic — no manual approval needed.

Subscriptions renew automatically each billing period. You don't need to do anything.`
            },
            {
                id: 'revenue-split',
                title: 'Revenue Split',
                content: `For every payment:

• **You keep: 85%** of the gross amount
• **Privly fee: 15%** collected automatically
• **Processing fees: ~2.9% + $0.30** from your portion

| Price | You Receive | Privly Fee | Processing |
| $5 | ~$3.95 | $0.75 | ~$0.30 |
| $10 | ~$7.90 | $1.50 | ~$0.60 |
| $25 | ~$19.77 | $3.75 | ~$1.48 |
| $50 | ~$39.55 | $7.50 | ~$2.95 |`
            },
            {
                id: 'failed-payments',
                title: 'Failed Payments',
                content: `If a renewal payment fails:

1. Subscriber enters a **3-day grace period** — they keep access
2. The payment is retried automatically
3. If still unpaid after 3 days, they're removed from the VIP channel
4. Status changes to "expired"

They can resubscribe at any time.`
            },
        ]
    },
    {
        id: 'chatters',
        title: 'Chatters & Team',
        articles: [
            {
                id: 'what-are-chatters',
                title: 'What are Chatters?',
                content: `Chatters are moderators you hire to manage subscriber conversations. They can respond to DMs, send paid content, and send tip requests on your behalf — helping you scale without being online 24/7.

When a chatter makes a sale (PPV or tip), it's attributed to them so you can track their performance.`
            },
            {
                id: 'inviting-chatters',
                title: 'Inviting a Chatter',
                content: `**Using invite codes**

1. Go to the **Marketplace** tab in your sidebar
2. Click **Create Invite Code**
3. Select which channels the chatter should have access to
4. Share the code with your chatter

The chatter enters the code on their dashboard to gain access to your channels.

**Connection requests**

Chatters can also find you in the marketplace and send a connection request. You'll see pending requests in the Marketplace tab and can accept or decline them.`
            },
            {
                id: 'chatter-permissions',
                title: 'What Chatters Can Do',
                content: `**Chatters CAN:**

• View and reply to subscriber DMs on your channels
• Send PPV (paid) content from your Vault
• Send tip requests to subscribers
• See their own earnings from sales they generate
• Chat with you via the Creator-Chatter messaging system

**Chatters CANNOT:**

• Change subscription plans or pricing
• Access your earnings or financial data
• Modify channel settings
• View other chatters' conversations or earnings
• Delete or modify your Vault files
• Access channels they haven't been assigned to`
            },
            {
                id: 'managing-chatters',
                title: 'Managing Your Team',
                content: `**Viewing performance**

In the Marketplace tab, you can see each chatter's stats — how many sales they've made and their total earnings.

**Removing a chatter**

To revoke a chatter's access, go to the Marketplace tab and remove them. They immediately lose access to your channels and can no longer send messages on your behalf.

**Communication**

Use the Creator-Chatter chat to discuss pricing, strategy, and send offers. You can send pricing offers directly in the chat — the chatter can accept or decline.`
            },
        ]
    },
];

const CHATTER_SECTIONS = [
    {
        id: 'chatter-getting-started',
        title: 'Getting Started',
        articles: [
            {
                id: 'chatter-what-is',
                title: 'What is a Chatter?',
                content: `A chatter helps creators manage subscriber conversations. Creators hire chatters to:

• Respond to subscriber DMs
• Send PPV (paid) content
• Send tip requests
• Manage conversations at scale

You earn a share of the sales you generate. When you send PPV and someone buys it, the sale is attributed to you.`
            },
            {
                id: 'chatter-registration',
                title: 'Creating Your Account',
                content: `1. Go to privly.co and sign up as a chatter
2. Choose a username and password
3. Complete your profile: display name, bio, languages, timezone
4. Set your availability and proposed rates

**Getting hired**

• Wait for a creator to find you in the marketplace
• Use an **invite code** from a creator
• Send **connection requests** to creators you'd like to work with`
            },
            {
                id: 'chatter-invite-code',
                title: 'Using an Invite Code',
                content: `If a creator gives you an invite code:

1. Go to your dashboard
2. Click **Add Channel**
3. Select the **Invite Code** tab
4. Enter the code and click **Redeem**

The creator's channels appear in your sidebar. You can start managing DMs immediately.`
            },
        ]
    },
    {
        id: 'chatter-chatting',
        title: 'Managing Conversations',
        articles: [
            {
                id: 'chatter-dm-basics',
                title: 'Responding to DMs',
                content: `Open the DMs tab to see subscriber conversations.

1. Click a conversation
2. Type your message
3. Press Enter or click Send

Your messages are sent through the bot. The subscriber sees them in their Telegram DM thread. Messages are marked with your chatter name so the creator knows who sent them.`
            },
            {
                id: 'chatter-ppv',
                title: 'Sending PPV Content',
                content: `You can sell content on behalf of the creator:

1. Open a conversation
2. Click the **$ icon**
3. Select a file from the creator's **Vault**
4. Set a price (minimum $3)
5. Add an optional caption
6. Click Send

The subscriber sees a blurred preview with an "Unlock" button. Sales are attributed to your account.`
            },
            {
                id: 'chatter-tips',
                title: 'Sending Tip Requests',
                content: `Prompt subscribers to send tips:

1. Open a conversation
2. Click the tip button
3. Choose a preset ($5, $10, $25, $50, $100) or set a custom amount
4. Optionally attach vault content
5. Send

Tips are voluntary — the subscriber can pay or ignore.`
            },
            {
                id: 'chatter-earnings',
                title: 'Viewing Your Earnings',
                content: `Your earnings are in the **Payments** section:

• **Total Earned** — All-time from sales attributed to you
• **Last 30 Days** — Recent performance
• **Transaction History** — Every sale with amount and date

Earnings are based on PPV and tip sales you generate.`
            },
        ]
    },
    {
        id: 'chatter-cc-chat',
        title: 'Creator-Chatter Chat',
        articles: [
            {
                id: 'cc-chat-basics',
                title: 'Chatting with Your Creator',
                content: `The CC chat lets you communicate directly with the creator who hired you.

• Send text messages (1 message per second limit)
• Receive and respond to pricing offers
• Track message status with delivery ticks

**Message ticks**

• Single grey tick — sent
• Double grey ticks — delivered
• Double blue ticks — read`
            },
        ]
    },
];

function renderContent(text) {
    const lines = text.split('\n');
    const elements = [];
    let inTable = false;
    let tableRows = [];

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) {
            if (inTable && tableRows.length > 0) {
                elements.push(
                    <div key={`table-${i}`} style={{ overflowX: 'auto', margin: '16px 0', borderRadius: '10px', border: '1px solid var(--border)' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                                <tr>{tableRows[0].map((h, j) => <th key={j} style={{ padding: '10px 16px', background: 'var(--bg, #f8fafc)', borderBottom: '1px solid var(--border)', textAlign: 'left', fontWeight: 600, color: 'var(--text)', fontSize: '12px' }}>{h}</th>)}</tr>
                            </thead>
                            <tbody>
                                {tableRows.slice(1).map((row, ri) => (
                                    <tr key={ri}>{row.map((cell, ci) => <td key={ci} style={{ padding: '10px 16px', borderBottom: ri < tableRows.length - 2 ? '1px solid var(--border)' : 'none', color: 'var(--text)', fontSize: '13px' }}>{cell}</td>)}</tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
                tableRows = [];
                inTable = false;
            }
            elements.push(<div key={`br-${i}`} style={{ height: '12px' }} />);
            continue;
        }

        if (line.startsWith('|')) {
            inTable = true;
            const cells = line.split('|').filter(c => c.trim()).map(c => c.trim());
            if (!cells.every(c => /^[-:]+$/.test(c))) tableRows.push(cells);
            continue;
        }

        if (line.startsWith('• ') || line.startsWith('- ')) {
            elements.push(
                <div key={i} style={{ display: 'flex', gap: '10px', padding: '3px 0', paddingLeft: '4px' }}>
                    <span style={{ color: 'var(--accent, #00AEEF)', fontSize: '18px', lineHeight: '22px', flexShrink: 0 }}>·</span>
                    <span style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--text)' }} dangerouslySetInnerHTML={{ __html: boldify(line.slice(2)) }} />
                </div>
            );
            continue;
        }

        if (/^\d+\.\s/.test(line)) {
            const num = line.match(/^(\d+)\.\s/)[1];
            elements.push(
                <div key={i} style={{ display: 'flex', gap: '12px', padding: '4px 0', paddingLeft: '4px' }}>
                    <span style={{ color: 'var(--accent, #00AEEF)', fontWeight: 700, fontSize: '13px', minWidth: '20px', flexShrink: 0 }}>{num}.</span>
                    <span style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--text)' }} dangerouslySetInnerHTML={{ __html: boldify(line.replace(/^\d+\.\s/, '')) }} />
                </div>
            );
            continue;
        }

        elements.push(
            <p key={i} style={{ margin: '4px 0', fontSize: '14px', lineHeight: 1.8, color: 'var(--text)' }} dangerouslySetInnerHTML={{ __html: boldify(line) }} />
        );
    }
    return elements;
}

function boldify(text) {
    return text
        .replace(/\*\*(.+?)\*\*/g, '<strong style="font-weight:600">$1</strong>')
        .replace(/`(.+?)`/g, '<code style="background:var(--input-bg, #f1f5f9);padding:2px 6px;border-radius:4px;font-size:12px;font-family:monospace">$1</code>');
}

export default function GuideTab({ isChatter = false, isMobile = false }) {
    const sections = isChatter ? CHATTER_SECTIONS : CREATOR_SECTIONS;
    const [activeArticle, setActiveArticle] = useState(sections[0]?.articles[0]?.id || '');
    const [search, setSearch] = useState('');
    const [expandedSections, setExpandedSections] = useState(new Set(sections.map(s => s.id)));

    const allArticles = useMemo(() => {
        const result = [];
        sections.forEach(section => {
            section.articles.forEach(article => {
                result.push({ ...article, sectionTitle: section.title, sectionId: section.id });
            });
        });
        return result;
    }, [sections]);

    const filteredArticles = useMemo(() => {
        if (!search.trim()) return null;
        const q = search.toLowerCase();
        return allArticles.filter(a => a.title.toLowerCase().includes(q) || a.content.toLowerCase().includes(q));
    }, [search, allArticles]);

    const currentArticle = allArticles.find(a => a.id === activeArticle) || allArticles[0];

    const toggleSection = (sectionId) => {
        setExpandedSections(prev => {
            const next = new Set(prev);
            if (next.has(sectionId)) next.delete(sectionId); else next.add(sectionId);
            return next;
        });
    };

    const SIcon = ({ id }) => {
        const Comp = SectionIcons[id];
        return Comp ? <Comp /> : null;
    };

    return (
        <div style={{ display: 'flex', height: '100%', background: 'var(--bg, #fff)' }}>
            {/* Sidebar */}
            {!isMobile && (
                <div style={{ width: '260px', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                    <div style={{ padding: '20px 16px 16px' }}>
                        <div style={{ position: 'relative' }}>
                            <Icons.Search style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', opacity: 0.4 }} />
                            <input
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Search..."
                                style={{ width: '100%', padding: '9px 12px 9px 34px', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--input-bg, #f8fafc)', color: 'var(--text)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                            />
                        </div>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 16px' }}>
                        {filteredArticles ? (
                            filteredArticles.length === 0 ? (
                                <p style={{ color: 'var(--text-muted)', fontSize: '13px', textAlign: 'center', padding: '32px 16px' }}>No results</p>
                            ) : filteredArticles.map(a => (
                                <div key={a.id} onClick={() => { setActiveArticle(a.id); setSearch(''); }}
                                    style={{ padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', marginBottom: '2px', background: activeArticle === a.id ? 'var(--surface-hover, rgba(0,0,0,0.04))' : 'transparent', transition: 'background 0.15s' }}
                                    onMouseEnter={e => { if (activeArticle !== a.id) e.currentTarget.style.background = 'rgba(0,0,0,0.02)'; }}
                                    onMouseLeave={e => { if (activeArticle !== a.id) e.currentTarget.style.background = 'transparent'; }}>
                                    <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text)' }}>{a.title}</div>
                                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{a.sectionTitle}</div>
                                </div>
                            ))
                        ) : sections.map(section => (
                            <div key={section.id} style={{ marginBottom: '2px' }}>
                                <div onClick={() => toggleSection(section.id)}
                                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', userSelect: 'none' }}>
                                    <span style={{ display: 'flex', color: 'var(--accent, #00AEEF)' }}><SIcon id={section.id} /></span>
                                    <span style={{ flex: 1 }}>{section.title}</span>
                                    <span style={{ fontSize: '8px', transform: expandedSections.has(section.id) ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', opacity: 0.5 }}>&#9654;</span>
                                </div>
                                {expandedSections.has(section.id) && section.articles.map(article => (
                                    <div key={article.id} onClick={() => setActiveArticle(article.id)}
                                        onMouseEnter={e => { if (activeArticle !== article.id) e.currentTarget.style.background = 'rgba(0,0,0,0.02)'; }}
                                        onMouseLeave={e => { if (activeArticle !== article.id) e.currentTarget.style.background = 'transparent'; }}
                                        style={{ padding: '6px 10px 6px 36px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', color: activeArticle === article.id ? 'var(--accent, #00AEEF)' : 'var(--text)', fontWeight: activeArticle === article.id ? 600 : 400, background: activeArticle === article.id ? 'var(--surface-hover, rgba(0,174,239,0.06))' : 'transparent', transition: 'all 0.15s', marginBottom: '1px' }}>
                                        {article.title}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '20px 16px' : '40px 56px' }}>
                {isMobile && (
                    <div style={{ marginBottom: '20px' }}>
                        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search guides..."
                            style={{ width: '100%', padding: '12px 16px', border: '1px solid var(--border)', borderRadius: '10px', background: 'var(--input-bg)', color: 'var(--text)', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                )}

                {isMobile && !search ? (
                    sections.map(section => (
                        <div key={section.id} style={{ marginBottom: '24px' }}>
                            <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ color: 'var(--accent, #00AEEF)' }}><SIcon id={section.id} /></span>{section.title}
                            </h3>
                            {section.articles.map(a => (
                                <div key={a.id} onClick={() => setActiveArticle(a.id)}
                                    style={{ padding: '14px 16px', marginBottom: '6px', borderRadius: '10px', border: '1px solid var(--border)', cursor: 'pointer', background: 'var(--card-bg, #fff)' }}>
                                    <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text)' }}>{a.title}</span>
                                </div>
                            ))}
                        </div>
                    ))
                ) : currentArticle && (
                    <div style={{ maxWidth: '640px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <span style={{ color: 'var(--accent, #00AEEF)' }}><SIcon id={allArticles.find(a => a.id === currentArticle.id)?.sectionId} /></span>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                {allArticles.find(a => a.id === currentArticle.id)?.sectionTitle}
                            </span>
                        </div>
                        <h1 style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700, color: 'var(--text)', margin: '0 0 24px', lineHeight: 1.2, letterSpacing: '-0.3px' }}>
                            {currentArticle.title}
                        </h1>
                        <div>{renderContent(currentArticle.content)}</div>
                    </div>
                )}
            </div>
        </div>
    );
}
